import React, { useState, useEffect, useMemo } from "react"
import { GridColDef, GridRowModel } from "@mui/x-data-grid"
// import {DefaultContainer}
import { useNavigate, useParams } from "react-router"
import { IconButton, Tooltip } from "@mui/material"
import { Folder } from "@mui/icons-material"
import { useLocation } from "../../../context/LocationContext"
import { useCompany } from "../../../context/CompanyContext"
import { useManageModal, TableToolBarStacked, SessionFilterOption, TablePaginated, FetchMoreCallbackArgs } from "ui"
import { GET_ANSWERS } from "../../../features/graphql/query"
import { useQuery } from "@apollo/client"
import { WHO_DO_YOU_LIVE_WITH_ENGLISH_QUESTION_ID, PARENT_DESCRIBE_THEIR_MOOD_ENGLISH_QUESTION_ID } from "../../../constants/QuestionIds/english-TweenScreenQuestionIds"
import { AnswerGroup } from "../../../__generated__/graphql"
import { splitCamelCase } from "../../../tools/splitToCamelCase"

const DEFAULT_PAGE_SIZE = 5;
function mapAnswersToTable(data: any, queryType: string) {
    const _answer = data.node
    const parsedData = _answer?.data ? JSON.parse(_answer.data) : null;
    var finalParsedData
    if (_answer.question.language == "English" && _answer.question.questionSet == "TweenScreen") {
        if (_answer.question.id === WHO_DO_YOU_LIVE_WITH_ENGLISH_QUESTION_ID) {
            finalParsedData = `${splitCamelCase(parsedData.data.guardianType)}: ${splitCamelCase(parsedData.data.livingSituation)}`
        }
        else if (_answer.question.id === PARENT_DESCRIBE_THEIR_MOOD_ENGLISH_QUESTION_ID) {
            const extractedTraits = parsedData.data?.flatMap((parent: any) => parent.characteristics || [])
            finalParsedData = extractedTraits.join(", ") || "N/A"
        }

    }

    return {
        ...data.node,
        id: _answer.id,
        questionPosition: _answer.question.position,
        screenLanguage: `${_answer.question.language} ${_answer.question.questionSet} `,
        question: _answer.question.title,
        questionType: _answer.question.type,
        category: _answer.question.category,
        data: finalParsedData ? finalParsedData : parsedData.skipped ? "Skipped question" : parsedData.notShown ? "Not Shown" : "N/A",
        sentimentAnalysisData: _answer?.sentimentAnalysisData,
        keywordExtractionData: _answer?.keywordExtractionData,

    }
}
const TABLE_COLUMNS: GridColDef[] = [
    {
        field: "questionPosition", headerName: "Question Number", width: 90,
        disableColumnMenu: true,
        renderHeader: () => {
            return <div style={{ whiteSpace: "break-spaces", fontWeight: 500, lineHeight: 1 }}>
                Question Number
            </div>
        }
    },
    {
        field: "screenLanguage", headerName: "Screen and Language", disableColumnMenu: true,
        renderHeader: () => {
            return <div style={{ whiteSpace: "break-spaces", fontWeight: 500, justifyContent: "center" }}>
                Screen Type
            </div>
        },
        renderCell: ({ value }) => {
            return <div style={{ whiteSpace: "break-spaces", }}>
                {value}
            </div>
        }
    },
    {
        field: "question", headerName: "Question", flex: 1,
        renderCell: ({ value }) => {
            return <div style={{ whiteSpace: "break-spaces" }}>
                {value}
            </div>
        }
    },
    {
        field: "questionType", headerName: "Question Type", width: 100,
        renderHeader: () => {
            return <div style={{ whiteSpace: "break-spaces", fontWeight: 500, }}>
                Question Type
            </div>
        }
    },
    {
        field: "category", headerName: "Question Category", width: 100,
        renderHeader: () => {
            return <div style={{ whiteSpace: "break-spaces", fontWeight: 500, }}>
                Question Category
            </div>
        }
    },

    { field: "data", headerName: "Answer", flex: 1 },
    // { field: "id", headerName: "Id", flex: 1 }
]
export function AnswerTable({ onBack }: { onBack: () => void }) {
    const { id } = useParams()
    const [pageLoading, setPageLoading] = useState<boolean>(true)
    // const { setAnswerInfo, setId, answerInfo, refetchAnswers, answers } = useAnswerContext()
    const navigate = useNavigate()
    const [tableFilter, setTableFilter] = useState("ALL")
    const windowLocation = window.location.pathname.includes("/location") ? "location" : window.location.pathname.includes("company") ? "company" : "answers"

    const { data, loading, fetchMore, refetch } = useQuery(GET_ANSWERS, {
        variables: {
            searchCriteria: windowLocation,
            searchId: id ?? "",
            first: DEFAULT_PAGE_SIZE,
            query: "",
            after: null,
            answerGroup: tableFilter as AnswerGroup
        }
    })
    const { openModal } = useManageModal()
    const { location } = useLocation()
    const { company } = useCompany()
    const [pageTableState, setPageTableState] = useState<{
        columns: GridColDef[],
        rows: GridRowModel[]
        filteredRows: GridRowModel[],
        title: string,
        subtitle: string | undefined,
        status: 'NORMAL' | "FILTERING"
    }>({ columns: [], rows: [], filteredRows: [], status: "NORMAL", title: "", subtitle: "" })

    const goToAnswerSentimentAndKeyword = (id: string, hasData: string) => {
        if (hasData.length > 0) {
            openModal({ type: "NO_DATA_TO_VIEW", cancelButtonLabel: "close" })
        } else {
            navigate(`/answers/${id}`)
        }

    }
    function getTableTitle() {
        if (windowLocation === "location") {
            return `Location Answers`
        } else if (windowLocation === "company") {
            return `Company Answers`
        } else {
            return "All Answers"
        }
    }
    function getTableSubTitle() {
        if (windowLocation === "location") {
            return `@${location?.name}`
        } else if (windowLocation === "company") {
            return `@${company?.name}`
        } else {
            return ""
        }
    }
    useEffect(() => {
        setPageTableState((prevState) => {
            const columns = [...TABLE_COLUMNS]
            columns.push({
                field: "sentimentAnalysisData", headerName: "Sentiment",
                headerAlign: "center", align: "center", width: 100, sortable: false,
                renderHeader: () => {
                    return <div style={{ whiteSpace: "break-spaces", fontWeight: 500, textAlign: "center" }}>
                        Sentiment and Keyword
                    </div>
                },
                renderCell: (params) => {
                    const id = params.row.id
                    const _activeMessage = params.row.sentimentAnalysisData || params.row.keywordExtractionData ? "" : "No Data for this answer"
                    return (
                        <Tooltip title={_activeMessage} >
                            <IconButton onClick={() => goToAnswerSentimentAndKeyword(id, _activeMessage)}>
                                <Folder />
                            </IconButton>
                        </Tooltip>
                    )
                }
            })
            if (data) {
                setPageLoading(false)
            }
            const rows = data?.answersByCondition?.edges?.map(mapAnswersToTable) ?? [];
            console.log("the rows in the useeffect data: ", rows)
            const _title = getTableTitle()
            const _subtitle = getTableSubTitle()
            console.log("the prev state: ", prevState)
            return {
                ...prevState,
                columns,
                filteredRows: [],
                rows,
                title: _title,
                subtitle: _subtitle
            }
        })

    }, [data, loading])

    const handleFilter = (option: SessionFilterOption) => {
        setTableFilter(option)
        console.log("filtering: ", option)
        refetch({
            searchCriteria: windowLocation,
            searchId: id ?? "",
            first: DEFAULT_PAGE_SIZE,
            query: "",
            after: null,
            answerGroup: option as AnswerGroup

        }).then((res) => {
            const rows = res?.data?.answersByCondition?.edges?.map(mapAnswersToTable) ?? [];
            console.log("the new data: ", res?.data)
            if (pageTableState.status === "FILTERING") {
                //if its filtering we don't want to mess with this, but we also want to call new things if it updates
                setPageTableState((prevState) => ({
                    ...prevState,
                    rows,
                }));
            } else {
                setPageTableState((prevState) => ({
                    ...prevState,
                    filteredRows: [],
                    rows,
                }));
            }
        }).catch(err => {
            console.log("there is an error")
        })
    }

    const goToCompanyAnswers = () => {
        if (windowLocation === "location") {
            const companyId = company?.id ?? location?.companyId
            navigate(`/company/${companyId}/answers`)
        }
    }
    const totalRows = pageTableState.status === "FILTERING"
        ? pageTableState.filteredRows.length
        : data?.answersByCondition?.totalCount
    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {
        await fetchMore({
            variables: {
                searchCriteria: windowLocation,
                searchId: id ?? "",
                first: variables.first ?? DEFAULT_PAGE_SIZE,
                after: variables.after ?? null,
                query: variables.query ?? "",
                answerGroup: tableFilter as AnswerGroup
            }
        }).then((res) => {
            const rows = res?.data?.answersByCondition?.edges?.map(mapAnswersToTable) ?? [];
            if (pageTableState.status === "FILTERING") {
                //if its filtering we don't want to mess with this, but we also want to call new things if it updates
                setPageTableState((prevState) => ({
                    ...prevState,
                    rows,
                }));
            } else {
                setPageTableState((prevState) => ({
                    ...prevState,
                    filteredRows: [],
                    rows,
                }));
            }
        }).catch(err => {
            console.log("there is an error")
        })
    }
    useEffect(() => {
        console.log("the data is changing: ", data)
    }, [data])
    return (
        <TablePaginated
            loading={loading}
            autoHeight
            rows={pageTableState.status === "FILTERING"
                ? pageTableState.filteredRows
                : pageLoading ? [] : pageTableState.rows}
            columns={pageTableState.columns}
            renderToolbar={({ searchQuery, onSearch, onSearchClear }) => (
                <TableToolBarStacked
                    title={pageTableState.title}
                    subTitle={pageTableState.subtitle}
                    // TODO: FOR A SEARCH DO WE WANT THIS?
                    onBack={onBack}
                    onReroute={windowLocation === "location" ? goToCompanyAnswers : undefined}
                    rerouteLabel={windowLocation === "location" ? "Go To Company Wide Answers" : ""}
                    onFilter={handleFilter}
                    onSearchClear={onSearchClear}
                    onSearch={onSearch}
                    searchQuery={searchQuery ?? ""}
                    filterTableName="sessions"
                />
            )}
            pageInfo={data?.answersByCondition?.pageInfo}
            pageSize={DEFAULT_PAGE_SIZE}
            totalRows={totalRows}
            filterParams={{ filterType: "answerCategory", filter: tableFilter }}
            onFetchMore={handleFetchMore}
        />
    )
}

